import { useMemo } from 'react'
import { Stack, Typography, ListItemButton, styled } from '@mui/material'
import { Timestamp } from '@weblite/ts-action/types'
import { LocationIcon } from '$common/icons/location'
import { formatSessionDate, getSessionInfo } from './helper'
import { t } from '$i18n'

interface StyledListItemButtonProps {
  isClickable: boolean
}

const StyledListItemButton = styled(ListItemButton)<StyledListItemButtonProps>`
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  padding: 13px 20px 14px;
  cursor: ${({ isClickable }) => (isClickable ? 'pointer' : 'default')};
`

const StyledLocationIcon = styled(LocationIcon)`
  margin-left: 4px;
  width: 18px;
  height: 18px;
`

interface SessionItemProps {
  ip?: string
  userAgent?: string
  date: Timestamp
  onClick?: () => unknown
}

export function SessionItem({
  ip = '',
  userAgent = '',
  date,
  onClick,
}: SessionItemProps) {
  const formattedDate = formatSessionDate(date)
  // NOTE: `getSessionInfo` runs a full user-agent parse. The list re-renders on
  // every terminate, so without this the whole list is re-parsed each time.
  const { os, device } = useMemo(() => getSessionInfo(userAgent), [userAgent])

  return (
    <StyledListItemButton
      disableRipple={!onClick}
      disableTouchRipple={!onClick}
      isClickable={!!onClick}
      onClick={onClick}
    >
      {device || os || ip ? (
        <Stack alignItems="flex-start">
          {device && <Typography variant="subtitle3">{device}</Typography>}
          {os && <Typography variant="body2">{os}</Typography>}

          {ip && (
            <Stack direction="row" justifyContent="center">
              <StyledLocationIcon sx={{ color: 'gray' }} />
              <Typography variant="body2">{ip}</Typography>
            </Stack>
          )}
        </Stack>
      ) : (
        <Typography variant="subtitle3">
          {t('activeSessions.unknownInformation')}
        </Typography>
      )}

      <Typography color="primary" variant="body2">
        {formattedDate}
      </Typography>
    </StyledListItemButton>
  )
}
